<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { trackingSurveyEvent } from '@/api/tracking';
import { PRODUCT_ICON_NAME } from '@/constants';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import Avatar from '@/components/common/atoms/avatar.vue';
import ActionBalloon from '@/components/common/molecules/balloon.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';
import { useTooltipVisibility } from '@/utils/composables/useTooltipVisibility';
import { SURVEY_HISTORIES_PAGE_NAME } from '@/utils/survey/constants';
import { useEmitter } from '@/utils/vue';
import { useStore } from '@/utils/vue';

interface Props {
  title: string;
  userName: string;
  userIconUrl?: string;
  useAnewsIcon?: boolean;
  updatedAt: string;
  sessionId: string;
  canCreateShareSurvey?: boolean;
}

const props = defineProps<Props>();
const router = useRouter();
const emitter = useEmitter();
const store = useStore();
const { createSnackbar } = useSnackbar();
const { getCurrentPathForTracking } = useCurrentPathForTracking();

const menuRef = ref<Element>();
const isClickOutside = useOutsideClick([menuRef]);
const { elementRef: titleRef, isTooltipVisible } = useTooltipVisibility();
watch(isClickOutside, isOutside => {
  if (isOutside) closeMenu();
  isClickOutside.value = false;
});

const isOpenMenu = ref(false);
const clickMenu = () => {
  isOpenMenu.value = !isOpenMenu.value;
};
const closeMenu = () => {
  isOpenMenu.value = false;
};

const isOpenLimitDialog = ref(false);

const createShareThread = async () => {
  if (!props.canCreateShareSurvey) return;
  try {
    const shareSurvey = await api.postShareSurvey(props.sessionId);
    createSnackbar({
      message: 'シェアスレッドを作成しました',
      type: 'success',
    });
    trackShareSurveyCreate(shareSurvey.uuid);
    emitter.emit('update-share-survey');
    router.push({
      name: 'shareSurvey',
      params: { uuid: shareSurvey.uuid },
    });
  } catch (error) {
    if (
      isAxiosError(error) &&
      error.response?.status === 400 &&
      error.response.data.error === 'user share survey limit reached'
    ) {
      isOpenLimitDialog.value = true;
      return;
    }
    createSnackbar({
      message: 'シェアスレッドの作成に失敗しました',
      type: 'error',
    });
    return;
  }
};
const trackShareSurveyCreate = (shareSurveyUuid: string) => {
  trackingSurveyEvent('share_survey_create', {
    page: {
      name: SURVEY_HISTORIES_PAGE_NAME,
      url: getCurrentPathForTracking(),
    },
    original_session_id: props.sessionId,
    session_id: shareSurveyUuid,
  });
};

const deleteHandler = () => {
  closeMenu();
  const payload = {
    headerText: 'スレッドの削除',
    bodyText: `「${props.title}」を削除しますか？`,
    action: deleteThread,
    btnText: '削除',
    cancelBtnText: 'キャンセル',
    isBtnDisabled: false,
  };
  store.commit('confirmationModal/setTextAndAction', payload);
  store.commit('confirmationModal/showConfirmationModal');
};

const deleteThread = async () => {
  try {
    await api.deleteSurveySession(props.sessionId);
    router.push({ name: 'surveyTop' });

    emitter.emit('delete-survey-session');

    createSnackbar({
      message: 'スレッドを削除しました',
      type: 'success',
    });
  } catch (_error) {
    createSnackbar({
      message: 'スレッドの削除に失敗しました',
      type: 'error',
    });
  }
};
</script>

<template>
  <div class="survey-header">
    <div class="survey-header-content">
      <div class="header-content-left">
        <VTooltip
          :disabled="!isTooltipVisible"
          placement="bottom"
          :prevent-overflow="true"
        >
          <div class="c-text-regular--m survey-header-title">
            <span ref="titleRef">
              {{ title }}
            </span>
          </div>
          <template #popper>{{ title }}</template>
        </VTooltip>
        <div class="survey-header-info c-text-regular--s">
          <div class="user-info">
            <div v-if="useAnewsIcon">
              <DgrIcon :name="PRODUCT_ICON_NAME" size="small" />
            </div>
            <Avatar v-else :custom-size="16" :image-url="userIconUrl" />
            <div>{{ userName }}</div>
          </div>
          <div class="updated-at">更新日：{{ updatedAt }}</div>
        </div>
      </div>
      <div class="header-content-right">
        <div class="header-menu">
          <button
            class="header-menu-button c-btn--auto c-btn"
            @click.stop.prevent="clickMenu"
          >
            <DgrIcon name="ellipsis-h" size="small" />
          </button>
          <div ref="menuRef">
            <ActionBalloon
              position="absolute"
              :is-show="isOpenMenu"
              balloon-position="right"
            >
              <div
                class="header-menu-item c-title-bold--m"
                @click="deleteHandler"
              >
                <DgrIcon name="trash" size="small" />
                スレッドを削除
              </div>
            </ActionBalloon>
          </div>
        </div>
        <button
          class="c-btn c-btn--auto c-btn--AnewsPrimary create-share-thread-button"
          :class="{ disabled: !canCreateShareSurvey }"
          :disabled="!canCreateShareSurvey"
          @click="createShareThread"
        >
          シェアスレッドを作成
        </button>
      </div>
    </div>
    <DgrDialog
      v-model:open="isOpenLimitDialog"
      title="シェアスレッドを作成"
      :has-footer="false"
    >
      <template #description>
        <div class="limit-dialog-content">
          シェアスレッドの上限数に達しているため作成することができませんでした。新たに作成するためには不要なシェアスレッドを削除してから再度実行してください。
        </div>
      </template>
    </DgrDialog>
  </div>
</template>

<style lang="scss" scoped>
.survey-header {
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  background-color: $color-white;
  .survey-header-content {
    max-width: var(--survey-session-width);
    width: 100%;
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }
  .header-content-left {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    .survey-header-title {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 532px;
    }
    .survey-header-info {
      padding: 4px 0;
      color: $color-gray800;
      display: flex;
      gap: 8px;
      .user-info {
        display: flex;
        gap: 4px;
      }
      .updated-at {
        white-space: nowrap;
      }
    }
  }
  .header-content-right {
    display: flex;
    align-items: center;
    gap: 10px;
    .share-button {
      white-space: nowrap;
    }
    .header-menu {
      position: relative;
    }
    .header-menu-button {
      padding: 11px;
      border-radius: 4px;
      border: none;
      &:hover {
        background: $color-gray200;
        cursor: pointer;
      }
    }
    .header-menu-item {
      width: 118px;
      padding: 5px 16px;
      display: flex;
      gap: 4px;
      justify-content: center;
      align-items: center;
      &:hover {
        background: $color-gray200;
        cursor: pointer;
      }
    }
    .create-share-thread-button {
      white-space: nowrap;
    }
  }
}
.limit-dialog-content {
  width: 624px;
  height: 249px;
}
</style>
